import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel,
  Select, MenuItem, FormGroup, FormControlLabel, Checkbox, Box, Typography, CircularProgress,
  Link, Divider
} from '@mui/material';
import api from '../../api/axios';
import { downloadBlob, filenameFromContentDisposition } from '../../utils/downloadBlob';

interface ColumnSpec {
  key: string;
  label: string;
  type?: string;
}

export type DatasetKey =
  | 'signups' | 'players' | 'affiliate_breakdown' | 'inactive_players'
  | 'affiliate_payouts' | 'affiliate_payout_players';

// Affiliate payout datasets use the payout period instead of the page date range.
export interface PayoutExportPeriod {
  period: 'week' | 'lifetime';
  start_date: string | null; // YYYY-MM-DD, week only
  end_date: string | null;
}

const isPayoutDataset = (key: DatasetKey) =>
  key === 'affiliate_payouts' || key === 'affiliate_payout_players';

interface ExportColumnsResponse {
  datasets: Partial<Record<DatasetKey, ColumnSpec[]>>;
}

const DATASET_LABELS: Record<DatasetKey, string> = {
  signups: 'New Sign-Ups',
  players: 'Player Breakdown',
  affiliate_breakdown: 'Affiliate Breakdown',
  inactive_players: 'Inactive Players',
  affiliate_payouts: 'Affiliate Payouts',
  affiliate_payout_players: 'Affiliate Payout Players',
};

interface AnalyticsExportDialogProps {
  open: boolean;
  onClose: () => void;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  tenantId?: number;
  onError?: (message: string) => void;
  // Dataset to preselect each time the dialog opens.
  initialDataset?: DatasetKey;
  // inactive_players only: these replace the date range.
  inactiveDays?: number;
  includeNeverPurchased?: boolean;
  // Payout datasets are offered only when this is set.
  payout?: PayoutExportPeriod;
  // IANA timezone for day-boundary-aware datasets (signups/players/affiliate_breakdown).
  tz?: string;
  // players dataset only: filters rows to purchasers/redeemers.
  activity?: 'all' | 'purchases' | 'redeems';
}

const AnalyticsExportDialog: React.FC<AnalyticsExportDialogProps> = ({
  open, onClose, startDate, endDate, tenantId, onError,
  initialDataset, inactiveDays = 30, includeNeverPurchased = false, payout, tz, activity,
}) => {
  const [schemas, setSchemas] = useState<Partial<Record<DatasetKey, ColumnSpec[]>> | null>(null);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [dataset, setDataset] = useState<DatasetKey>('signups');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState(false);

  // Load the column catalog once when the dialog first opens.
  useEffect(() => {
    if (!open || schemas) return;
    const load = async () => {
      setLoadingSchemas(true);
      try {
        const res = await api.get('/api/v1/admin/analytics/export/columns');
        const data = res.data as ExportColumnsResponse;
        setSchemas(data.datasets);
      } catch (error: any) {
        onError?.(error.response?.data?.detail || 'Failed to load export columns');
      } finally {
        setLoadingSchemas(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && initialDataset) setDataset(initialDataset);
  }, [open, initialDataset]);

  const isInactive = dataset === 'inactive_players';
  const isPayout = isPayoutDataset(dataset) && !!payout;
  const payoutLabel = payout?.period === 'week' && payout.start_date && payout.end_date
    ? `${payout.start_date}_${payout.end_date}`
    : 'lifetime';
  const columns: ColumnSpec[] = schemas ? schemas[dataset] || [] : [];

  // When dataset changes (or columns first load), select all columns by default.
  useEffect(() => {
    if (!columns.length) return;
    const next: Record<string, boolean> = {};
    columns.forEach((c) => { next[c.key] = true; });
    setSelected(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, schemas]);

  const selectedKeys = columns.filter((c) => selected[c.key]).map((c) => c.key);

  const toggleColumn = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setAll = (value: boolean) => {
    const next: Record<string, boolean> = {};
    columns.forEach((c) => { next[c.key] = value; });
    setSelected(next);
  };

  const handleDownload = async () => {
    if (!selectedKeys.length) {
      onError?.('Select at least one column');
      return;
    }
    setDownloading(true);
    try {
      // Build the query manually so `columns` is sent as repeated params
      // (columns=a&columns=b), which is what the backend expects.
      const params = new URLSearchParams();
      params.append('dataset', dataset);
      if (isInactive) {
        // The inactive report is relative to now; the date range doesn't apply.
        params.append('days', inactiveDays.toString());
        params.append('include_never_purchased', String(includeNeverPurchased));
      } else if (isPayout && payout) {
        params.append('period', payout.period);
        if (payout.period === 'week' && payout.start_date && payout.end_date) {
          params.append('start_date', payout.start_date);
          params.append('end_date', payout.end_date);
        }
      } else {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
        if (tz) params.append('tz', tz);
        if (dataset === 'players' && activity && activity !== 'all') params.append('activity', activity);
      }
      if (tenantId) params.append('tenant_id', tenantId.toString());
      selectedKeys.forEach((key) => params.append('columns', key));

      const res = await api.get(`/api/v1/admin/analytics/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const fallback = isInactive
        ? `inactive_players_${inactiveDays}d.csv`
        : isPayout
          ? `${dataset}_${payoutLabel}.csv`
          : dataset === 'players' && activity && activity !== 'all'
            ? `${dataset}_${activity}_${startDate}_${endDate}.csv`
            : `${dataset}_${startDate}_${endDate}.csv`;
      const filename = filenameFromContentDisposition(
        (res.headers as any)?.['content-disposition'],
        fallback,
      );
      downloadBlob(res.data as Blob, filename);
      onClose();
    } catch (error: any) {
      // Blob error responses need to be read back as text to surface the detail.
      let message = 'Failed to export CSV';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed.detail || message;
        } catch { /* keep default */ }
      } else if (data?.detail) {
        message = data.detail;
      }
      onError?.(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Download CSV Report</DialogTitle>
      <DialogContent dividers>
        {loadingSchemas ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Dataset</InputLabel>
              <Select
                value={dataset}
                label="Dataset"
                onChange={(e) => setDataset(e.target.value as DatasetKey)}
              >
                {(Object.keys(DATASET_LABELS) as DatasetKey[])
                  // Add-on datasets are omitted from the catalog when not enabled; don't tease them.
                  .filter((key) => key !== 'inactive_players' || !!schemas?.inactive_players)
                  // Payout datasets need the payout period, which only the payouts section passes;
                  // that section offers nothing else.
                  .filter((key) => isPayoutDataset(key) === !!payout)
                  .map((key) => (
                  <MenuItem key={key} value={key} disabled={!schemas || !schemas[key]}>
                    {DATASET_LABELS[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="caption" color="textSecondary">
              {isInactive
                ? `No purchase in the last ${inactiveDays} days${includeNeverPurchased ? ' (incl. never purchased)' : ''} · date range not applied`
                : isPayout
                  ? (payout?.period === 'week' ? `Payout week: ${payout.start_date} → ${payout.end_date}` : 'Lifetime')
                  : `Range: ${startDate} → ${endDate}`}
              {tenantId ? ` · Tenant ${tenantId}` : ''}
            </Typography>

            <Divider />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">Columns ({selectedKeys.length} selected)</Typography>
              <Box>
                <Link component="button" variant="body2" onClick={() => setAll(true)} sx={{ mr: 2 }}>
                  Select all
                </Link>
                <Link component="button" variant="body2" onClick={() => setAll(false)}>
                  Clear
                </Link>
              </Box>
            </Box>

            <FormGroup>
              {columns.map((c) => (
                <FormControlLabel
                  key={c.key}
                  control={<Checkbox checked={!!selected[c.key]} onChange={() => toggleColumn(c.key)} />}
                  label={c.label}
                />
              ))}
              {!columns.length && (
                <Typography variant="body2" color="textSecondary">No columns available.</Typography>
              )}
            </FormGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={downloading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={downloading || loadingSchemas || !selectedKeys.length}
        >
          {downloading ? <CircularProgress size={24} /> : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnalyticsExportDialog;
